import type {
  Platform,
  PlatformConfig,
  MetaPlatform,
  CreatePlatformInput,
  UpdatePlatformInput,
  ApiResponse,
  HealthCheck,
} from '../types';

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'https://cardql-core.finhub.workers.dev';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      const data = (await response.json()) as ApiResponse<T> & { error?: string };

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return data as ApiResponse<T>;
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  // Health Check
  async healthCheck(): Promise<ApiResponse<HealthCheck>> {
    return this.request<HealthCheck>('/health');
  }

  // Platform Management (TO BE IMPLEMENTED IN BACKEND)
  // These endpoints need to be added to cardql-core worker

  /**
   * List all platforms under the AppEdge meta-platform
   * Backend endpoint: GET /admin/platforms
   */
  async listPlatforms(): Promise<ApiResponse<Platform[]>> {
    // TODO: This endpoint needs to be implemented in cardql-core
    // For now, return empty array or mock data for development
    return this.request<Platform[]>('/admin/platforms');
  }

  /**
   * Get a specific platform by ID
   * Backend endpoint: GET /admin/platforms/:id
   */
  async getPlatform(id: string): Promise<ApiResponse<Platform>> {
    return this.request<Platform>(`/admin/platforms/${id}`);
  }

  /**
   * Create a new platform
   * Backend endpoint: POST /admin/platforms
   */
  async createPlatform(
    input: CreatePlatformInput
  ): Promise<ApiResponse<Platform>> {
    return this.request<Platform>('/admin/platforms', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  /**
   * Update an existing platform
   * Backend endpoint: PATCH /admin/platforms/:id
   */
  async updatePlatform(
    id: string,
    input: UpdatePlatformInput
  ): Promise<ApiResponse<Platform>> {
    return this.request<Platform>(`/admin/platforms/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  /**
   * Delete a platform
   * Backend endpoint: DELETE /admin/platforms/:id
   */
  async deletePlatform(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/admin/platforms/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get platform configuration
   * Backend endpoint: GET /admin/platforms/:id/config
   */
  async getPlatformConfig(id: string): Promise<ApiResponse<PlatformConfig>> {
    return this.request<PlatformConfig>(`/admin/platforms/${id}/config`);
  }

  /**
   * Get meta-platform details
   * Backend endpoint: GET /admin/meta-platform
   */
  async getMetaPlatform(): Promise<ApiResponse<MetaPlatform>> {
    return this.request<MetaPlatform>('/admin/meta-platform');
  }

  /**
   * Execute AppEdge migration (one-time setup)
   * Backend endpoint: POST /admin/migrate/appedge
   */
  async executeAppEdgeMigration(dryRun: boolean = false): Promise<ApiResponse<any>> {
    return this.request<any>(`/admin/migrate/appedge${dryRun ? '?dryRun=true' : ''}`, {
      method: 'POST',
      headers: {
        'x-api-key': import.meta.env.VITE_ADMIN_API_KEY || '',
      },
    });
  }
}

export const apiClient = new ApiClient();

// Export individual methods for easier imports
export const {
  healthCheck,
  listPlatforms,
  getPlatform,
  createPlatform,
  updatePlatform,
  deletePlatform,
  getPlatformConfig,
  getMetaPlatform,
  executeAppEdgeMigration,
} = apiClient;
